import React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import chroma from 'chroma-js';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Legend,
  Tooltip as ChartTooltip,
  ReferenceLine,
  Label,
} from 'recharts';

import Scenario from '../../api/Scenario';
import Header from '../../components/utils/Header';
import {
  formatGrowth, formatUnit,
  formatVolume,
  unitFormatter,
} from '../../utils';
import FilterListView from '../../components/FilterListView';
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import ConditionalFormat from '../../components/utils/ConditionalFormat';
import DemandSwitches from '../../store/DemandSwitches';
import DemandSwitchesPanel from '../../components/DemandSwitchesPanel';
import DataFrameDownload from '../../components/utils/DataFrameDownload';
import Cycle, { cycle } from '../../store/Cycle';
import { athenaRed } from '../../globals';


const styles = theme => ({
  chartContainer: {
    overflow: 'hidden',
  },
  tablePaper: {
    overflow: 'auto',
  },
  tableSeparator: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  subtotalColor: {
    backgroundColor: theme.palette.grey[100],
  },
  totalColor: {
    backgroundColor: theme.palette.grey[300],
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
});


@withStyles(styles)
@withRouter
@observer
class DemandView extends BaseScenarioDashboardView {
  static apiSubPath = '/demand_overview/';

  @observable scenario = null;
  @observable geographicalLevel = null;
  @observable dateLevel = null;

  constructor(props) {
    super(props);
    this.switches = new DemandSwitches({}, this.updateData);
  }

  _setData(data) {
    this.scenario = data.scenario;
    this.switches.set('Base demand in scope', data.base_demand_in_scope);
    this.switches.set('Base demand out of scope', data.base_demand_out_scope);
    this.switches.set('Innovation in scope', data.innovation_demand);
    this.switches.set('Risks & opps in scope', data.risks_opps_demand);
    this.geographicalLevel = data.geographical_level;
    this.dateLevel = data.date_level;
  }

  _getQueryParams() {
    return {
      scenario: this.scenario,
      base_demand_in_scope: this.switches.get('Base demand in scope'),
      base_demand_out_scope: this.switches.get('Base demand out of scope'),
      innovation_demand: this.switches.get('Innovation in scope'),
      risks_opps_demand: this.switches.get('Risks & opps in scope'),
      geographical_level: this.geographicalLevel,
      date_level: this.dateLevel,
    };
  }

  getColor(ratio) {
    return chroma.scale([
      '#2C488F',
      '#466BC6',
      '#95AADF',
      '#0194BA',
      '#26D0FE',
    ])(ratio).hex();
  }

  onScenarioChange = this.genericChangeHandler('scenario');
  onGeographicalLevelChange = this.genericChangeHandler('geographicalLevel');

  @action onDateLevelChange = event => {
    this.dateLevel = event.target.checked ? 'month' : 'quarter';
    this.updateData();
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          <ScenarioSelect
            scenarioId={this.scenario}
            choices={this.data.scenario_choices}
            onScenarioChange={this.onScenarioChange}
          />
        </Grid>
        <Grid item>
          <Typography noWrap>
            Quarterly
            <Switch
              checked={this.dateLevel === 'month'}
              onChange={this.onDateLevelChange}
            />
            Monthly
          </Typography>
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            'Geographical level', this.data.geographical_level_choices,
            this.geographicalLevel, this.onGeographicalLevelChange,
          )}
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/demand_overview/?' + this.getQueryString())}
            permission="athena.download_demand_overview" 
          />
        </Grid>
      </Grid>
    );
  }

  get chart() {
    if (!this.ready) {
      return <Skeleton variant="rect" height="100%" />;
    }
    const { geographies } = this.data;
    const scenario = Scenario.getForId(this.scenario);
    const scenarioCycle = scenario.cycle === null ? cycle : scenario.cycle;
    const actualsEndCycle = Cycle.fromOther(scenarioCycle, -1);
    const apoEndCycle = Cycle.fromOther(scenarioCycle, scenario.optimization_month_start);
    return (
      <ResponsiveContainer aspect={21 / 9}>
        <BarChart data={[...this.data.graph_data]} margin={{ top: 16, left: 30 }}>
          <XAxis dataKey={this.dateLevel} />
          <YAxis tickFormatter={unitFormatter('t', 0)} />
          <ChartTooltip formatter={unitFormatter('t', 0)} />
          {geographies.map((geography, index) =>
            this.getBar(
              geography,
              { key: geography, stackId: 'a', fill: this.getColor((index + 1) / geographies.length) },
            )
          )}
          <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
          <ReferenceLine
            x={actualsEndCycle.toString()}
            position="end"
            label={<Label position="top" fill={athenaRed}>Actuals end</Label>}
            strokeDasharray="3 3"
            stroke={athenaRed}
            isFront
          />
          <ReferenceLine
            x={apoEndCycle.toString()}
            position="start"
            label={<Label position="top" fill={athenaRed}>APO forecast end</Label>}
            strokeDasharray="3 3"
            stroke={athenaRed}
            isFront
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  get table() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const { classes } = this.props;
    const floatColumns = [
      'Y estimation at the time of the cycle',
      'Y+1 estimation at the time of the cycle',
    ];
    const growthColumns = [
      'Comparison with Y estimation of previous cycle',
      'Comparison with Y+1 estimation of previous cycle',
      'Comparison with Y-1',
      'Comparison with Y',
    ];
    const colorColumns = [
      'Comparison with Y estimation of previous cycle',
      'Comparison with Y+1 estimation of previous cycle',
    ];
    const absoluteGrowthColumns = [];
    const absoluteGrowthBinding = {};
    for (const growthColumn of growthColumns) {
      const absoluteGrowthColumn = 'Abs ' + growthColumn;
      absoluteGrowthColumns.push(absoluteGrowthColumn);
      absoluteGrowthBinding[growthColumn] = absoluteGrowthColumn;
    }
    const displayedColumns = Object.keys(this.data.table_data[0]).filter(
      key => absoluteGrowthColumns.indexOf(key) === -1
    );
    const format = (row, key) => {
      let value = row[key];
      if (floatColumns.indexOf(key) !== -1) {
        return formatVolume(value, 't', 0);
      }
      if (growthColumns.indexOf(key) !== -1) {
        const formattedGrowth = formatGrowth(value);
        const absoluteGrowth = row[absoluteGrowthBinding[key]];
        const content = `${formatUnit(absoluteGrowth, 't', 0)} (${formattedGrowth})`;
        if (colorColumns.indexOf(key)!==-1) {
          return (
            <ConditionalFormat
              value={value}
              name={this.geographicalLevel === 'Market' ? 'demand-overview-market' : 'demand-overview-macro-zone'}
            >
              {content}
            </ConditionalFormat>
          );
        }
        return content;
      }
      return value;
    };
    const isIndex = key => key === this.geographicalLevel;
    const getCellClass = key => (
      isIndex(key) || (key === 'Comparison with Y-1')
        ? classes.tableSeparator
        : null
    );

    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };

    return (
      <Paper className={classes.tablePaper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {displayedColumns.map(key => (
                <TableCell
                  key={key}
                  align={isIndex(key) ? 'left' : 'right'}
                  className={getCellClass(key)}
                >
                  {key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {this.data.table_data.map((row, index) => (
              <TableRow key={index} className={paintRow(row['Macro Zone'])}>
                {displayedColumns.map(key =>
                  <TableCell
                    key={key}
                    variant={isIndex(key) ? 'head' : null}
                    align={isIndex(key) ? 'left' : 'right'}
                    className={getCellClass(key)}
                  >
                    {format(row, key)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Demand overview" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        <Grid item>
          <DemandSwitchesPanel switches={this.switches} />
        </Grid>
        <Grid item className={classes.chartContainer}>
          {this.chart}
        </Grid>
        <Grid item>
          {this.table}
        </Grid>
      </Grid>
    );
  }
}


export default DemandView;
